import * as React from 'react';
import { useMemo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import {
  ChatBubbleLeftRightIcon,
  DevicePhoneMobileIcon,
  EllipsisHorizontalIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';

import { cn, customImageLoader, useResponsiveBreakPoints } from '@restworld/utils-common';
import { Button, DropdownItem, Dropdown, Heading, IconButton } from '@restworld/ui-ds-base';
import { LinkBase } from '@restworld/ui-components';

export function RestworldManagerCard({
  iconUrl,
  email,
  name,
  surname,
  phoneNumber,
  openIntercom,
}: RestworldManagerCardProps) {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsiveBreakPoints();

  const actionItems: DropdownItem[] = useMemo(
    () => [
      { label: t('call_the_manager'), Icon: DevicePhoneMobileIcon, href: `tel:${phoneNumber}` },
      { label: t('send_an_email'), Icon: EnvelopeIcon, href: `mailto:${email}` },
    ],
    [email, phoneNumber, t]
  );

  return (
    <MainWrapper>
      <div>
        <div className="flex justify-between items-start">
          <ImageAndNameWrapper>
            <ImageWrapper>{iconUrl && <Image src={iconUrl} alt="" fill loader={customImageLoader} />}</ImageWrapper>
            <Heading size={5} weight="semibold" color="neutral">
              {`${name} ${surname}`}
            </Heading>
          </ImageAndNameWrapper>
          {isMobile && (
            <Dropdown
              placement="top-end"
              items={actionItems}
              triggerElement={<IconButton Icon={EllipsisHorizontalIcon} ariaLabel="view more" size="sm" />}
            />
          )}
        </div>
        {phoneNumber && (
          <LinkBase newTab={false} link={`tel:${phoneNumber}`} className={'hidden md:block'}>
            <div className="flex items-center gap-1 w-fit mb-3">
              <DevicePhoneMobileIcon className="h-4 w-4 text-neutral-200" />
              <Heading as="span" size={5} weight="medium" color="neutral-light">
                {phoneNumber}
              </Heading>
            </div>
          </LinkBase>
        )}
        {email && (
          <LinkBase newTab={false} link={`mailto:${email}`} className={'hidden md:block'}>
            <div className="flex items-center gap-1 w-fit">
              <EnvelopeIcon className="h-4 w-4 text-neutral-200" />
              <Heading as="span" size={5} weight="medium" color="neutral-light">
                {email}
              </Heading>
            </div>
          </LinkBase>
        )}
      </div>
      <Button
        StartIcon={ChatBubbleLeftRightIcon}
        variant={isMobile ? 'secondary-neutral' : 'tertiary'}
        fullwidth
        onClick={openIntercom}
        size="sm"
      >
        {t('open_chat')}
      </Button>
    </MainWrapper>
  );
}
export default RestworldManagerCard;

interface RestworldManagerCardProps {
  iconUrl?: string;
  name?: string;
  surname?: string;
  phoneNumber?: string;
  email?: string;
  openIntercom?: () => void;
}

const MainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'border-neutral-40 rounded-xl p-5 w-full flex flex-col justify-between gap-8 flex-1 h-full bg-fluo-100 md:bg-neutral-10 md:border',
      className
    )}
    {...props}
  />
);
const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('rounded-full overflow-hidden h-8 w-8 relative', className)} {...props} />
);
const ImageAndNameWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2 mb-3 md:flex-row', className)} {...props} />
);
